/**
 * Alshallal Pastry Company HR System - Repository Page
 * Phase 21: Archived Users & Employees Repository
 * 
 * Access: Super Admin ONLY
 * 
 * Features:
 * - View archived users and employees
 * - Restore archived entities
 * - Purge (permanently delete) archived entities
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { 
  Search, 
  RotateCcw,
  Trash2,
  Loader2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Archive,
  User,
  Users,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RepositoryPage() {
  const { token, user } = useAuth();
  const { t, isRTL } = useLanguage();
  
  // State
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('user');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Dialog state
  const [restoreDialog, setRestoreDialog] = useState({ open: false, record: null });
  const [purgeDialog, setPurgeDialog] = useState({ open: false, records: [] });
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Create axios instance with auth
  const api = useMemo(() => {
    return axios.create({
      baseURL: API_URL,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  }, [token]);
  
  // Fetch records
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/admin/repository/records', {
        params: {
          entity_type: activeTab,
          page,
          page_size: 20
        }
      });
      
      setRecords(response.data.records || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
      setSelectedIds([]);
    } catch (err) {
      console.error('Failed to fetch repository records:', err);
      setError(err.response?.data?.detail?.error || 'Failed to load repository');
    } finally {
      setLoading(false);
    }
  }, [api, activeTab, page]);
  
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);
  
  // Handle tab change
  const handleTabChange = (value) => {
    setActiveTab(value);
    setPage(1);
    setSelectedIds([]);
  };
  
  // Handle selection
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(records.map(r => r.id));
    } else {
      setSelectedIds([]);
    }
  };
  
  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(i => i !== id));
    }
  };
  
  // Handle restore
  const handleRestore = async () => {
    if (!restoreDialog.record) return;
    
    setActionLoading(true);
    try {
      await api.post('/api/admin/repository/restore', {
        archive_record_id: restoreDialog.record.id
      });
      
      toast.success(isRTL ? 'تم الاستعادة بنجاح' : 'Restored successfully');
      setRestoreDialog({ open: false, record: null });
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail?.error || 'Failed to restore');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle purge
  const handlePurge = async () => {
    const recordsToPurge = purgeDialog.records;
    const expectedToken = `PURGE ${recordsToPurge.length}`;
    
    if (purgeConfirm.toUpperCase() !== expectedToken.toUpperCase()) {
      toast.error(isRTL 
        ? `يرجى كتابة "${expectedToken}" للتأكيد`
        : `Please type "${expectedToken}" to confirm`
      );
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await api.post('/api/admin/repository/purge', {
        archive_record_ids: recordsToPurge.map(r => r.id),
        confirm_token: purgeConfirm
      });
      
      if (response.data.success) {
        toast.success(isRTL 
          ? `تم حذف ${response.data.deleted?.length || 0} سجل(ات) نهائياً`
          : `Permanently deleted ${response.data.deleted?.length || 0} record(s)`
        );
      } else {
        toast.error(response.data.failed?.[0]?.error || 'Purge failed');
      }
      
      setPurgeDialog({ open: false, records: [] });
      setPurgeConfirm('');
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail?.error || 'Failed to purge');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Open purge dialog for selected
  const handleBulkPurge = () => {
    const selected = records.filter(r => selectedIds.includes(r.id));
    if (selected.length === 0) {
      toast.error(isRTL ? 'يرجى اختيار سجلات للحذف' : 'Please select records to purge');
      return;
    }
    setPurgeDialog({ open: true, records: selected });
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Get entity display name
  const getEntityName = (record) => {
    const summary = record.entity_summary || {};
    if (record.entity_type === 'user') {
      return summary.email || `${summary.first_name || ''} ${summary.last_name || ''}`.trim() || 'Unknown';
    }
    return `${summary.first_name || ''} ${summary.last_name || ''}`.trim() || 'Unknown';
  };
  
  // Get role/job display
  const getRoleOrJob = (record) => {
    const summary = record.entity_summary || {};
    if (record.entity_type === 'user') {
      return summary.role || '-';
    }
    return summary.job_title || '-';
  };
  
  // Filter records by search
  const filteredRecords = useMemo(() => {
    if (!search) return records;
    const searchLower = search.toLowerCase();
    return records.filter(r => {
      const name = getEntityName(r).toLowerCase();
      return name.includes(searchLower);
    });
  }, [records, search]);
  
  // Check access
  if (user?.role !== 'super_admin') {
    return (
      <Card className="max-w-lg mx-auto mt-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            {isRTL ? 'الوصول مرفوض' : 'Access Denied'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            {isRTL 
              ? 'فقط المسؤول الأعلى يمكنه الوصول إلى المستودع'
              : 'Only Super Admin can access the repository'
            }
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Archive className="h-6 w-6" />
            {isRTL ? 'المستودع' : 'Repository'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isRTL 
              ? 'عرض واستعادة المستخدمين والموظفين المؤرشفين'
              : 'View and restore archived users and employees'
            }
          </p>
        </div>
        
        <Button variant="outline" onClick={fetchRecords} disabled={loading}>
          <RefreshCw className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2", loading && "animate-spin")} />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="user" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            {isRTL ? 'المستخدمين' : 'Users'}
          </TabsTrigger>
          <TabsTrigger value="employee" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {isRTL ? 'الموظفين' : 'Employees'}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                {/* Search */}
                <div className="relative w-full sm:w-64">
                  <Search className={cn(
                    "absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground",
                    isRTL ? "right-3" : "left-3"
                  )} />
                  <Input
                    placeholder={isRTL ? 'بحث...' : 'Search...'}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={cn("w-full", isRTL ? "pr-9" : "pl-9")}
                  />
                </div>
                
                {/* Bulk Actions */}
                {selectedIds.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {isRTL 
                        ? `${selectedIds.length} محدد`
                        : `${selectedIds.length} selected`
                      }
                    </span>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkPurge}
                    >
                      <Trash2 className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
                      {isRTL ? 'حذف نهائي' : 'Purge'}
                    </Button>
                  </div>
                )}
              </div>
              
              <CardDescription>
                {isRTL 
                  ? `إجمالي السجلات المؤرشفة: ${total}`
                  : `Total archived records: ${total}`
                }
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : error ? (
                <div className="text-center py-12 text-red-500">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                  <p>{error}</p>
                  <Button variant="outline" onClick={fetchRecords} className="mt-4">
                    {isRTL ? 'إعادة المحاولة' : 'Retry'}
                  </Button>
                </div>
              ) : filteredRecords.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Archive className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{isRTL ? 'لا توجد سجلات مؤرشفة' : 'No archived records'}</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <Checkbox
                              checked={selectedIds.length === filteredRecords.length && filteredRecords.length > 0}
                              onCheckedChange={handleSelectAll}
                            />
                          </TableHead>
                          <TableHead>{isRTL ? 'الاسم/البريد' : 'Name/Email'}</TableHead>
                          <TableHead>{isRTL ? 'الدور/المسمى' : 'Role/Title'}</TableHead>
                          <TableHead>{isRTL ? 'تاريخ الأرشفة' : 'Archived At'}</TableHead>
                          <TableHead>{isRTL ? 'بواسطة' : 'By'}</TableHead>
                          <TableHead>{isRTL ? 'السبب' : 'Reason'}</TableHead>
                          <TableHead className="text-right">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRecords.map((record) => (
                          <TableRow key={record.id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedIds.includes(record.id)}
                                onCheckedChange={(checked) => handleSelectOne(record.id, checked)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              {getEntityName(record)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {getRoleOrJob(record)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(record.archived_at)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {record.archived_by_name || '-'}
                            </TableCell>
                            <TableCell className="text-sm max-w-[200px] truncate">
                              {record.reason || '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setRestoreDialog({ open: true, record })}
                                  disabled={record.restored_at}
                                >
                                  <RotateCcw className={cn("h-4 w-4", isRTL ? "ml-1" : "mr-1")} />
                                  {isRTL ? 'استعادة' : 'Restore'}
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => setPurgeDialog({ open: true, records: [record] })}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        {page} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Restore Dialog */}
      <Dialog open={restoreDialog.open} onOpenChange={(open) => setRestoreDialog({ open, record: restoreDialog.record })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              {isRTL ? 'استعادة السجل' : 'Restore Record'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'سيتم استعادة هذا السجل إلى الحالة النشطة'
                : 'This record will be restored to active status'
              }
            </DialogDescription>
          </DialogHeader>
          
          {restoreDialog.record && (
            <div className="py-4">
              <p className="font-medium">{getEntityName(restoreDialog.record)}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {restoreDialog.record.entity_type === 'user' ? (isRTL ? 'مستخدم' : 'User') : (isRTL ? 'موظف' : 'Employee')}
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreDialog({ open: false, record: null })}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={handleRestore} disabled={actionLoading}>
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isRTL ? 'استعادة' : 'Restore'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Purge Dialog */}
      <Dialog open={purgeDialog.open} onOpenChange={(open) => { setPurgeDialog({ open, records: purgeDialog.records }); setPurgeConfirm(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {isRTL ? 'حذف نهائي' : 'Permanent Delete'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'تحذير: هذا الإجراء لا يمكن التراجع عنه. سيتم حذف البيانات نهائياً.'
                : 'Warning: This action cannot be undone. Data will be permanently deleted.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 font-medium">
                {isRTL 
                  ? `سيتم حذف ${purgeDialog.records.length} سجل(ات) نهائياً`
                  : `${purgeDialog.records.length} record(s) will be permanently deleted`
                }
              </p>
              <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                {purgeDialog.records.slice(0, 5).map(r => (
                  <li key={r.id}>{getEntityName(r)}</li>
                ))}
                {purgeDialog.records.length > 5 && (
                  <li>... {isRTL ? `و ${purgeDialog.records.length - 5} آخرين` : `and ${purgeDialog.records.length - 5} more`}</li>
                )}
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="purge-confirm">
                {isRTL 
                  ? `اكتب "PURGE ${purgeDialog.records.length}" للتأكيد:`
                  : `Type "PURGE ${purgeDialog.records.length}" to confirm:`
                }
              </Label>
              <Input
                id="purge-confirm"
                value={purgeConfirm}
                onChange={(e) => setPurgeConfirm(e.target.value)}
                placeholder={`PURGE ${purgeDialog.records.length}`}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPurgeDialog({ open: false, records: [] }); setPurgeConfirm(''); }}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={handlePurge}
              disabled={actionLoading || purgeConfirm.toUpperCase() !== `PURGE ${purgeDialog.records.length}`}
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isRTL ? 'حذف نهائي' : 'Purge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
